import React, { useEffect, useRef, useState } from 'react';

interface DecryptedTextProps {
  text: string;
  className?: string;
  speed?: number;
  maxIterations?: number;
  sequential?: boolean;
  revealDirection?: 'start' | 'end' | 'center';
  characters?: string;
  onAnimationComplete?: () => void;
  animateOn?: 'view' | 'hover' | 'mount';
}

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%&*<>/\\|`~';

const DecryptedText: React.FC<DecryptedTextProps> = ({
  text,
  className = '',
  speed = 50,
  maxIterations = 10,
  sequential = true,
  revealDirection = 'start',
  characters = CHARS,
  onAnimationComplete,
  animateOn = 'mount',
}) => {
  const [displayed, setDisplayed] = useState<string[]>(() => text.split(''));
  const [revealed, setRevealed] = useState<boolean[]>(() => new Array(text.length).fill(false));
  const ref = useRef<HTMLSpanElement>(null);
  const iterationsRef = useRef<number[]>([]);

  useEffect(() => {
    iterationsRef.current = new Array(text.length).fill(0);
  }, [text]);

  const startAnimation = () => {
    const chars = text.split('');
    const iters = new Array(chars.length).fill(0);
    const done = new Array(chars.length).fill(false);
    setRevealed(new Array(chars.length).fill(false));
    setDisplayed(chars.map(() => characters[Math.floor(Math.random() * characters.length)]));

    let currentIndex = 0;

    const interval = setInterval(() => {
      setDisplayed((prev) => {
        const next = [...prev];
        if (sequential) {
          // Reveal one character at a time in sequence
          if (currentIndex < chars.length) {
            iters[currentIndex] = (iters[currentIndex] ?? 0) + 1;
            if (iters[currentIndex] >= maxIterations) {
              done[currentIndex] = true;
              next[currentIndex] = chars[currentIndex];
              currentIndex++;
            } else {
              next[currentIndex] = characters[Math.floor(Math.random() * characters.length)];
            }
          }
        } else {
          chars.forEach((ch, i) => {
            if (!done[i]) {
              iters[i] = (iters[i] ?? 0) + 1;
              if (iters[i] >= maxIterations) {
                done[i] = true;
                next[i] = ch;
              } else {
                next[i] = characters[Math.floor(Math.random() * characters.length)];
              }
            }
          });
        }
        return next;
      });

      setRevealed([...done]);

      if (done.every(Boolean)) {
        clearInterval(interval);
        onAnimationComplete?.();
      }
    }, speed);

    return () => clearInterval(interval);
  };

  useEffect(() => {
    if (animateOn === 'mount') return startAnimation();
    if (animateOn === 'view') {
      const el = ref.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { observer.disconnect(); startAnimation(); } },
        { threshold: 0.1 }
      );
      observer.observe(el);
      return () => observer.disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, animateOn]);

  return (
    <span ref={ref} className={`inline ${className}`} aria-label={text}>
      {displayed.map((ch, i) => (
        <span
          key={i}
          className={revealed[i] ? '' : 'text-primary-400/70'}
          style={{ display: 'inline-block', minWidth: ch === ' ' ? '0.3em' : undefined }}
        >
          {ch}
        </span>
      ))}
    </span>
  );
};

export default DecryptedText;
